import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FiArrowRight } from "react-icons/fi";
import Loader from "../loader/Loader";
import {
  verifyEmailLessLogin,
  verifyPhoneLessLogin,
} from "../../redux/actions/AuthUserActions";
import "../../views/styles/flow-form.css";

const EMAIL = "email";
const PHONE = "phone";

const toE164ish = (raw) => {
  if (!raw) return "";
  let value = String(raw).replace(/[^\d+]/g, "");
  if (!value.startsWith("+")) value = "+" + value;
  return value;
};

// Sign-in by one-time code, shared by the order flows and the formalities
function PasswordlessLogin({ header, otpPath, signupPath, notice }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [method, setMethod] = useState(EMAIL);
  const [submitting, setSubmitting] = useState(false);

  const schema = useMemo(
    () =>
      Yup.object({
        email:
          method === EMAIL
            ? Yup.string()
                .email("L'adresse e-mail est invalide.")
                .required("Saisissez votre adresse e-mail.")
            : Yup.string().notRequired(),
        phone:
          method === PHONE
            ? Yup.string()
                .transform((v) => (v ? v.replace(/[^\d+]/g, "") : v))
                .matches(/^(\+?\d{6,20})$/, "Le numéro est invalide.")
                .required("Saisissez votre numéro de téléphone.")
            : Yup.string().notRequired(),
      }),
    [method],
  );

  const handleCallback = ({ success, message }, values) => {
    setSubmitting(false);

    if (!success) {
      Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      }).fire({ icon: "error", title: "Échec", text: message });
      return;
    }

    navigate(otpPath, {
      state: {
        method: values.method,
        email: values.method === EMAIL ? values.email : undefined,
        phone: values.method === PHONE ? values.phone : undefined,
      },
    });
  };

  const formik = useFormik({
    initialValues: { email: "", phone: "" },
    validationSchema: schema,
    onSubmit: (values) => {
      setSubmitting(true);

      if (method === EMAIL) {
        dispatch(
          verifyEmailLessLogin(values, (response) =>
            handleCallback(response, { ...values, method }),
          ),
        );
      } else {
        const payload = { phone: toE164ish(values.phone) };
        dispatch(
          verifyPhoneLessLogin(payload, (response) =>
            handleCallback(response, { ...payload, method }),
          ),
        );
      }
    },
  });

  const error = formik.touched[method] && formik.errors[method];

  return (
    <div className="flow-form-page">
      {header}
      <Loader isLoading={submitting} />

      <header className="flow-form-header">
        <h1 className="flow-form-title">
          Connectez-vous à votre <span>compte</span>
        </h1>
        <p className="flow-form-lead">
          Nous vous envoyons un code de connexion, sans mot de passe.
        </p>
        {notice && (
          <p className="flow-notice" role="status">
            {notice}
          </p>
        )}
      </header>

      <form className="flow-form" onSubmit={formik.handleSubmit} noValidate>
        <div
          className="flow-switch"
          role="tablist"
          aria-label="Recevoir le code par"
        >
          {[
            [EMAIL, "E-mail"],
            [PHONE, "Téléphone"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={method === value}
              className={method === value ? "is-active" : ""}
              onClick={() => setMethod(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {method === EMAIL ? (
          <div className="flow-field">
            <label htmlFor="login-email" className="flow-label">
              Adresse e-mail
            </label>
            <input
              id="login-email"
              type="email"
              name="email"
              className={`flow-input ${error ? "is-invalid" : ""}`}
              placeholder="vous@exemple.com"
              autoComplete="email"
              inputMode="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {error ? (
              <p className="flow-error">{error}</p>
            ) : (
              <p className="flow-hint">Le code vous est envoyé par e-mail.</p>
            )}
          </div>
        ) : (
          <div className="flow-field">
            <label htmlFor="login-phone" className="flow-label">
              Numéro de téléphone
            </label>
            <PhoneInput
              country="cm"
              value={formik.values.phone}
              onChange={(value) => formik.setFieldValue("phone", value)}
              onBlur={() => formik.setFieldTouched("phone", true)}
              inputProps={{
                id: "login-phone",
                name: "phone",
                autoComplete: "tel",
              }}
              inputClass="flow-phone"
              containerClass="flow-phone-container"
            />
            {error ? (
              <p className="flow-error">{error}</p>
            ) : (
              <p className="flow-hint">Le code vous est envoyé par SMS.</p>
            )}
          </div>
        )}

        <div className="flow-actions">
          <button type="submit" className="flow-submit" disabled={submitting}>
            Recevoir mon code <FiArrowRight aria-hidden="true" />
          </button>
        </div>

        {signupPath && (
          <p className="flow-alt">
            Pas encore de compte ? <Link to={signupPath}>Créer un compte</Link>
          </p>
        )}
      </form>
    </div>
  );
}

export default PasswordlessLogin;
